import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ExcelJS from "exceljs";
import { Popup } from "devextreme-react/popup";
import { Download, MoreHorizontal, Search } from "lucide-react";

import "./SupervisionTestResults.css";
import "./SupervisionScheduling.css";
import { handleGetLocal } from "../../services/auth-service";

const API = process.env.REACT_APP_API_URL;

const columns = [
  { key: "participantId", label: "Participant Id" },
  { key: "altId", label: "Alt Id" },
  { key: "lastName", label: "Last Name" },
  { key: "firstName", label: "First Name" },
  { key: "agency", label: "Agency" },
  { key: "supervisor", label: "Supervisor" },
  { key: "testDate", label: "Test Date", date: true },
  { key: "lastTestDate", label: "Last Test Date", date: true },
  { key: "frequency", label: "Frequency" },
  { key: "gender", label: "Gender" },
];

const pad = (n) => String(n).padStart(2, "0");

const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const initialRange = () => {
  const now = new Date();

  return {
    startDate: toDateKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    endDate: toDateKey(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

const dateKey = (value) => {
  if (!value) return "";

  const text = String(value);

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) return `${us[3]}-${pad(us[1])}-${pad(us[2])}`;

  return "";
};

const displayDate = (value) => {
  const key = dateKey(value);
  if (!key) return "—";

  const [year, month, day] = key.split("-");
  return `${month}/${day}/${year}`;
};

const mapSchedule = (item) => {
  const fullName = String(item.sLastFirst ?? "");
  const comma = fullName.indexOf(",");

  const lastName = item.sLastName || (comma >= 0 ? fullName.slice(0, comma).trim() : "");

  const firstName = comma >= 0 ? fullName.slice(comma + 1).trim() : fullName;

  return {
    scheduleId: item.gDrugTestRandomizerScheduleId,
    personId: item.gPersonID,
    docketId: item.gDocketID,

    participantId: item.sOffenderNumber ?? "",
    altId: item.sOffenderNumberPIN ?? "",
    lastName,
    firstName,

    agency: item.sAgencyName ?? item.sTestLocation ?? "",
    supervisor: item.sPrimarySupervisorName ?? "",

    testDate: dateKey(item.dTest || item.sTestDate),
    lastTestDate: dateKey(item.dLastTest),

    frequency: item.sFrequencyName ?? "",
    gender: item.sGender === "M" ? "Male" : item.sGender === "F" ? "Female" : (item.sGender ?? ""),

    testTime: item.sTestTime ?? "",
    testSlot: item.iSlot,
    location: item.sTestLocation ?? "",
    checkedIn: item.bCheckedIn === true,
    voided: item.bVoid === true,
    appointmentReference: item.appointmentReference ?? null,

    timeSlot: item.sTestTime ?? null,

    appointmentStatus: item.bVoid === true ? "Voided" : item.dTest || item.sTestDate ? "Scheduled" : "—",
  };
};

const exportValue = (item, col) =>
  col.date ? (item[col.key] ? displayDate(item[col.key]) : "") : (item[col.key] ?? "");

const safeText = (value) => {
  const text = String(value ?? "");
  return /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
};

const saveCsv = (rows) => {
  const escapeCsv = (value) => `"${safeText(value).replace(/"/g, '""')}"`;

  const lines = [
    columns.map((col) => escapeCsv(col.label)).join(","),
    ...rows.map((row) => columns.map((col) => escapeCsv(exportValue(row, col))).join(",")),
  ];

  const blob = new Blob(["\uFEFF", lines.join("\r\n")], {
    type: "text/csv;charset=utf-8;",
  });

  downloadBlob(blob, "SupervisionScheduling.csv");
};

const downloadBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = name;

  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const saveExcel = async (rows) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Scheduling");

  sheet.columns = columns.map((col) => ({
    header: col.label,
    key: col.key,
    width: ["agency", "supervisor", "frequency"].includes(col.key) ? 28 : 19,
  }));

  const heading = sheet.getRow(1);
  heading.font = { bold: true, color: { argb: "FF203956" } };
  heading.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEEF3F9" },
  };
  heading.height = 26;

  rows.forEach((item) => {
    const record = {};

    columns.forEach((col) => {
      record[col.key] = safeText(exportValue(item, col));
    });

    sheet.addRow(record);
  });

  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };

  sheet.views = [{ state: "frozen", ySplit: 1 }];

  const buffer = await workbook.xlsx.writeBuffer();

  downloadBlob(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "SupervisionScheduling.xlsx",
  );
};

export default function SupervisionScheduling() {
  const [locations, setLocations] = useState([]);
  const [locationLoading, setLocationLoading] = useState(true);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [draftRange, setDraftRange] = useState(initialRange);
  const [activeRange, setActiveRange] = useState(initialRange);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({
    key: "testDate",
    direction: "asc",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [menuId, setMenuId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [voidRow, setVoidRow] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidError, setVoidError] = useState("");
  const [voidSaving, setVoidSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Domain: handleGetLocal("pathURL"),
  });

  // Load permitted scheduling locations.
  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      setLocationLoading(true);

      try {
        const response = await axios.get(`${API}/api/RandomDrugTesting/get-supervision-locations`, {
          headers: headers(),
        });

        if (response.data?.isSuccess === false) {
          throw new Error(response.data?.message || "Unable to load locations.");
        }

        const list = Array.isArray(response.data?.data) ? response.data.data : [];

        if (cancelled) return;

        setLocations(list);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Unable to load scheduling locations.");
        }
      } finally {
        if (!cancelled) setLocationLoading(false);
      }
    };

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, []);

  // Load schedules for every court assigned to this user.
  useEffect(() => {
    if (locationLoading) return;

    if (locations.length === 0) {
      setResults([]);
      return;
    }

    let cancelled = false;

    const loadSchedules = async () => {
      setLoading(true);
      setError("");

      try {
        const requests = locations.map((location) =>
          axios.get(`${API}/api/RandomDrugTesting/get-supervision-drugtest-schedule`, {
            headers: headers(),
            params: {
              Location: location.key,
              dStart: activeRange.startDate,
              dEnd: activeRange.endDate,
            },
          })
        );

        // Reject the entire refresh if any authorized court request fails.
        const responses = await Promise.all(requests);
        if (cancelled) return;

        const combined = [];
        for (const response of responses) {
          if (response.data?.isSuccess !== true) {
            throw new Error(response.data?.message || "Unable to load scheduling.");
          }
          if (!Array.isArray(response.data?.data)) {
            throw new Error("Unexpected scheduling response.");
          }
          combined.push(...response.data.data);
        }

        // Protect against duplicate schedule rows returned by overlapping locations.
        const uniqueSchedules = new Map();
        for (const item of combined) {
          if (item.bVoid === true) continue;
          const id = item.gDrugTestRandomizerScheduleId;
          if (id) uniqueSchedules.set(String(id).toLowerCase(), item);
        }

        setResults(Array.from(uniqueSchedules.values()).map(mapSchedule));
        setPage(1);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          setError(
            err.response?.data?.message ||
            err.message ||
            "Unable to retrieve scheduled tests."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSchedules();
    return () => { cancelled = true; };
  }, [locations, locationLoading, activeRange, refreshKey]);

  const filteredResults = useMemo(() => {
    const term = search.trim().toLowerCase();

    return results
      .filter((item) => {
        if (item.testDate < activeRange.startDate || item.testDate > activeRange.endDate) {
          return false;
        }

        const getValue = (key) =>
          String(
            columns.find((col) => col.key === key)?.date ? displayDate(item[key]) : (item[key] ?? ""),
          ).toLowerCase();

        if (term && !columns.some((col) => getValue(col.key).includes(term))) {
          return false;
        }

        return columns.every((col) =>
          getValue(col.key).includes(
            String(filters[col.key] ?? "")
              .trim()
              .toLowerCase(),
          ),
        );
      })
      .sort((a, b) => {
        const comparison = String(a[sort.key] ?? "").localeCompare(String(b[sort.key] ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });

        return sort.direction === "asc" ? comparison : -comparison;
      });
  }, [results, search, filters, sort, activeRange]);

  const pageCount = Math.max(1, Math.ceil(filteredResults.length / pageSize));

  const currentPage = Math.min(page, pageCount);

  const visibleResults = filteredResults.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const applyDates = () => {
    if (!draftRange.startDate || !draftRange.endDate) {
      setError("Please select both dates.");
      return;
    }

    if (draftRange.startDate > draftRange.endDate) {
      setError("Start Date cannot be after End Date.");
      return;
    }

    setError("");
    setSuccess("");
    setPage(1);
    setActiveRange({ ...draftRange });
  };

  const toggleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const openVoid = (row) => {
    setMenuId(null);
    setVoidReason("");
    setVoidError("");
    setVoidRow(row);
  };

  const saveVoid = async () => {
    if (!voidRow?.scheduleId) {
      setVoidError("Invalid Drug Test Schedule ID.");
      return;
    }

    if (!voidReason.trim()) {
      setVoidError("Please enter a detailed void reason.");
      return;
    }

    setVoidSaving(true);
    setVoidError("");

    try {
      const response = await axios.post(
        `${API}/api/RandomDrugTesting/void-drugtest-schedule`,
        {
          gDrugTestRandomizerScheduleID: voidRow.scheduleId,
          EnteredReason: voidReason.trim(),
        },
        { headers: headers() },
      );

      if (response.data?.isSuccess !== true) {
        throw new Error(response.data?.responseMessage || response.data?.message || "Unable to void the test.");
      }

      setVoidRow(null);
      setSuccess(response.data?.responseMessage || "Scheduled drug test voided successfully.");

      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setVoidError(
        err.response?.data?.responseMessage ||
          err.response?.data?.message ||
          err.message ||
          "Unable to void the scheduled test.",
      );
    } finally {
      setVoidSaving(false);
    }
  };

  const detail = selected ?? {};
  const voidDetail = voidRow ?? {};

  const detailFields = [
    ["Participant Id", detail.participantId],
    ["Alt Id", detail.altId],

    ["Last Name", detail.lastName],
    ["First Name", detail.firstName],

    ["Agency", detail.agency],
    ["Supervisor", detail.supervisor],

    ["Test Date", displayDate(detail.testDate)],
    ["Last Test Date", displayDate(detail.lastTestDate)],

    ["Frequency", detail.frequency],
    ["Gender", detail.gender],

    ["Appointment Reference", detail.appointmentReference],
    ["Time Slot", detail.timeSlot],
    ["Appointment Status", detail.appointmentStatus],
  ];

  return (
    <div className="str-page ss-page">
      <div className="str-heading">
        <h2>Drug Test Schedule</h2>
        <p>Records available under your Pronto permissions.</p>
      </div>

      <div className="str-date-bar">
        <div className="str-date-field">
          <label htmlFor="ss-start">Start Date</label>
          <input
            id="ss-start"
            type="date"
            value={draftRange.startDate}
            onChange={(e) =>
              setDraftRange((prev) => ({
                ...prev,
                startDate: e.target.value,
              }))
            }
          />
        </div>

        <div className="str-date-field">
          <label htmlFor="ss-end">End Date</label>
          <input
            id="ss-end"
            type="date"
            value={draftRange.endDate}
            onChange={(e) =>
              setDraftRange((prev) => ({
                ...prev,
                endDate: e.target.value,
              }))
            }
          />
        </div>

        <button type="button" className="str-btn" onClick={applyDates}>
          Apply Dates
        </button>

        <span className="str-date-help">Filter by scheduled Test Date</span>


      </div>

      {error && (
        <p className="str-error" role="alert">
          {error}
        </p>
      )}

      {success && (
        <p className="ss-success" role="status">
          {success}
        </p>
      )}

      <div className="str-card">
        <div className="str-toolbar">
          <span className="str-toolbar-title">Scheduling</span>

          <div className="str-export-actions">
            <button
              type="button"
              className="str-btn"
              disabled={loading || locationLoading || locations.length === 0}
              onClick={async () => {
                try {
                  await saveExcel(filteredResults);
                } catch (err) {
                  console.error(err);
                  setError("Unable to export scheduling to Excel.");
                }
              }}
            >
              <Download size={14} />
              Export to Excel
            </button>

            <button
              type="button"
              className="str-btn"
              disabled={loading || locationLoading || locations.length === 0}
              onClick={() => saveCsv(filteredResults)}
            >
              <Download size={14} />
              Export to CSV
            </button>
          </div>

          <div className="str-search">
            <Search size={16} />
            <input
              placeholder="Search scheduling..."
              aria-label="Search scheduling"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {locationLoading && <p className="str-loading">Loading locations...</p>}

        {!locationLoading && locations.length === 0 && !error && (
          <p className="ss-notice">No authorized scheduling courts are available.</p>
        )}

        {loading && <p className="str-loading">Loading scheduled tests...</p>}

        <div className="str-table-scroll">
          <table className="str-table">
            <thead>
              <tr>
                <th>Actions</th>

                {columns.map((col) => (
                  <th key={col.key} className="str-sortable" onClick={() => toggleSort(col.key)}>
                    {col.label}
                    <span>{sort.key === col.key ? (sort.direction === "asc" ? " ↑" : " ↓") : " ↕"}</span>
                  </th>
                ))}
              </tr>

              <tr className="str-filter-row">
                <th />

                {columns.map((col) => (
                  <th key={col.key}>
                    <input
                      aria-label={`Filter ${col.label}`}
                      placeholder="Filter..."
                      value={filters[col.key] ?? ""}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          [col.key]: e.target.value,
                        }));
                        setPage(1);
                      }}
                    />
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibleResults.map((item) => (
                <tr key={item.scheduleId}>
                  <td>
                    <div className="str-action-wrap">
                      <button
                        type="button"
                        className="str-action-button"
                        aria-label={`Actions for ${item.participantId}`}
                        onClick={() => setMenuId(menuId === item.scheduleId ? null : item.scheduleId)}
                      >
                        <MoreHorizontal size={17} />
                      </button>

                      {menuId === item.scheduleId && (
                        <div className="str-action-menu ss-action-menu">
                          <button
                            type="button"
                            onClick={() => {
                              setSelected(item);
                              setMenuId(null);
                            }}
                          >
                            View Detail
                          </button>

                          {!item.voided && (
                            <button type="button" onClick={() => openVoid(item)}>
                              Void Drug Test
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>

                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.key === "participantId" ? (
                        <button type="button" className="str-id-link" onClick={() => setSelected(item)}>
                          {item.participantId || "—"}
                        </button>
                      ) : col.date ? (
                        displayDate(item[col.key])
                      ) : (
                        item[col.key] || "—"
                      )}
                    </td>
                  ))}
                </tr>
              ))}

              {!loading && visibleResults.length === 0 && (
                <tr>
                  <td className="str-empty" colSpan={columns.length + 1}>
                    No scheduled tests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="str-footer">
          <div className="str-page-sizes">
            {[10, 20, 30, 50, 100].map((size) => (
              <button
                type="button"
                key={size}
                className={`str-page-size-btn ${pageSize === size ? "active" : ""}`}
                onClick={() => {
                  setPageSize(size);
                  setPage(1);
                }}
              >
                {size}
              </button>
            ))}
          </div>

          <div className="str-pagination-right">
            <span className="str-page-summary">
              Page {currentPage} of {pageCount} ({filteredResults.length} items)
            </span>

            <div className="str-page-controls">
              <button
                type="button"
                className="str-page-arrow"
                disabled={currentPage === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                ‹
              </button>

              <button type="button" className="str-page-number active" aria-current="page">
                {currentPage}
              </button>

              <button
                type="button"
                className="str-page-arrow"
                disabled={currentPage === pageCount}
                onClick={() => setPage((prev) => Math.min(pageCount, prev + 1))}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* VIEW DETAIL POPUP */}

      <Popup
        visible={selected !== null}
        onHiding={() => setSelected(null)}
        showTitle={false}
        showCloseButton={false}
        hideOnOutsideClick={true}
        dragEnabled={false}
        width="min(660px, 95vw)"
        height={600}
        maxHeight="85vh"
        shading={true}
        shadingColor="rgba(30, 47, 70, 0.56)"
      >
        <div className="str-view-detail-popup">
          <div className="str-view-detail-header">
            <div>
              <h3>Scheduled Test Details</h3>
              <p>
                {[detail.firstName, detail.lastName].filter(Boolean).join(" ")}
                {detail.participantId ? ` · ${detail.participantId}` : ""}
              </p>
            </div>

            <button className="str-btn" type="button" onClick={() => setSelected(null)}>
              Close
            </button>
          </div>

          <div className="str-view-detail-divider" />

          <div className="str-view-detail-grid">
            {detailFields.map(([label, value]) => (
              <div className="str-view-detail-field" key={label}>
                <label>{label}</label>
                <p>{value === 0 ? 0 : value || "—"}</p>
              </div>
            ))}
          </div>

          <div className="str-view-detail-footer">Read-only scheduled test information.</div>
        </div>
      </Popup>

      {/* VOID DRUG TEST POPUP */}

      <Popup
        visible={voidRow !== null}
        onHiding={() => {
          if (!voidSaving) setVoidRow(null);
        }}
        showTitle={false}
        showCloseButton={false}
        hideOnOutsideClick={false}
        dragEnabled={false}
        width="min(660px, 95vw)"
        height={400}
        maxHeight="85vh"
        shading={true}
        shadingColor="rgba(30, 47, 70, 0.56)"
      >
        <div className="ss-void-popup">
          {/* Header */}
          <div className="ss-void-header">
            <div>
              <h3>Void Drug Test</h3>
              <p>
                {[voidDetail.firstName, voidDetail.lastName].filter(Boolean).join(" ")}
                {voidDetail.participantId ? ` · ${voidDetail.participantId}` : ""}
              </p>
            </div>

            <button
              type="button"
              className="str-btn ss-void-close"
              disabled={voidSaving}
              onClick={() => setVoidRow(null)}
            >
              Close
            </button>
          </div>

          <div className="ss-void-divider" />

          {/* Body */}
          <div className="ss-void-content">
            <p className="ss-void-date">Scheduled Test Date: {displayDate(voidDetail.testDate)}</p>

            <div className="ss-void-reason-field">
              <label htmlFor="ss-void-reason">Void Reason</label>

              <input
                id="ss-void-reason"
                type="text"
                value={voidReason}
                disabled={voidSaving}
                placeholder=""
                onChange={(e) => {
                  setVoidReason(e.target.value);
                  setVoidError("");
                }}
              />

              {voidError && (
                <p className="ss-void-error" role="alert">
                  {voidError}
                </p>
              )}
            </div>

            <p className="ss-void-info">Voiding a scheduled test cannot be undone. Enter a reason before submitting.</p>

            <button type="button" className="str-btn ss-void-submit" disabled={voidSaving} onClick={saveVoid}>
              {voidSaving ? "Saving..." : "Void Drug Test"}
            </button>
          </div>

          {/* Footer */}
          <div className="ss-void-footer">Changes will be saved to Pronto when submitted.</div>
        </div>
      </Popup>
    </div>
  );
}
